import React from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 99999;
`;

const fireworkAnimation = keyframes`
  0% {
    transform: translate(var(--x), var(--initialY));
    width: var(--initialSize);
    opacity: 1;
  }
  50% {
    width: 0.5vmin;
    opacity: 1;
  }
  100% {
    width: var(--finalSize);
    opacity: 0;
  }
`;

// Baemingo POS colors
// const COLOR_SET_1 = ['#ffe7cb', '#ff9357', '#f4b0c0', '#ff80a7', '#fecbff', '#fc7dff'];
// const COLOR_SET_2 = ['#d3a4fe', '#c17bff', '#c2d0ff', '#93a5ff', '#87dbff', '#2cbbf8'];
// const COLOR_SET_3 = ['#bcfffb', '#3bffff', '#7dffd0', '#0dd38c', '#b1ffa2', '#83ff6d'];

// Colorful set
// const COLOR_SET_1 = ['#ff595e', '#ff924c', '#ffca3a', '#8ac926', '#1982c4', '#6a4c93'];
// const COLOR_SET_2 = ['#8ac926', '#1982c4', '#ff924c', '#6a4c93', '#ff595e', '#ffca3a'];
// const COLOR_SET_3 = ['#ff924c', '#6a4c93', '#8ac926', '#1982c4', '#ffca3a', '#ff595e'];

// Neon set
const COLOR_SET_1 = ['#70d6ff', '#ff70a6', '#ff9770', '#ffd670', '#e9ff70', '#94ff70'];
const COLOR_SET_2 = ['#ffd670', '#e9ff70', '#ff70a6', '#94ff70', '#70d6ff', '#ff9770'];
const COLOR_SET_3 = ['#ff70a6', '#94ff70', '#ffd670', '#e9ff70', '#ff9770', '#70d6ff'];

// Function to generate CSS variables from the colors array
const generateColorVars = (colors: string[]) => {
  return colors.map((color, index) => `--color${index + 1}: ${color};`).join('\n');
};

const Firework = styled.div<{
  colors: string[];
  x: string;
  y: string;
  top: string;
  left: string;
  animationDelay: string;
}>`
  --initialSize: 0.7vmin;
  --finalSize: 45vmin;
  --particleSize: 0.3vmin;
  --y: ${(props) => props.y || '-30vmin'};
  --x: ${(props) => props.x || '-50%'};
  --initialY: 60vmin;
  content: '';
  animation: ${fireworkAnimation} 1.2s ${(props) => props.animationDelay || '0'} 2;
  position: absolute;
  top: ${(props) => props.top || '50%'};
  left: ${(props) => props.left || '50%'};
  opacity: 0;
  transform: translate(-50%, var(--y));
  width: var(--initialSize);
  aspect-ratio: 1;
  background: radial-gradient(circle, var(--color1) var(--particleSize), #0000 0) 50% 0%,
    radial-gradient(circle, var(--color2) var(--particleSize), #0000 0) 100% 50%,
    radial-gradient(circle, var(--color3) var(--particleSize), #0000 0) 50% 100%,
    radial-gradient(circle, var(--color4) var(--particleSize), #0000 0) 0% 50%,
    radial-gradient(circle, var(--color5) var(--particleSize), #0000 0) 80% 90%,
    radial-gradient(circle, var(--color6) var(--particleSize), #0000 0) 95% 90%,
    radial-gradient(circle, var(--color1) var(--particleSize), #0000 0) 90% 70%,
    radial-gradient(circle, var(--color2) var(--particleSize), #0000 0) 100% 60%,
    radial-gradient(circle, var(--color3) var(--particleSize), #0000 0) 55% 80%,
    radial-gradient(circle, var(--color1) var(--particleSize), #0000 0) 45% 20%,
    radial-gradient(circle, var(--color1) var(--particleSize), #0000 0) 33% 34%,
    radial-gradient(circle, var(--color1) var(--particleSize), #0000 0) 10% 29%,
    radial-gradient(circle, var(--color1) var(--particleSize), #0000 0) 31% 37%,
    radial-gradient(circle, var(--color1) var(--particleSize), #0000 0) 28% 7%,
    radial-gradient(circle, var(--color1) var(--particleSize), #0000 0) 13% 42%;
  background-size: var(--initialSize) var(--initialSize);
  background-repeat: no-repeat;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: var(--initialSize);
    aspect-ratio: 1;
    background: inherit;
    background-size: inherit;
    background-repeat: inherit;
    animation: inherit;
  }

  &::before {
    --x: -50%;
    --y: -50%;
    --initialY: -50%;
    transform: translate(-50%, -50%) rotate(40deg) scale(1.3) rotateY(40deg);
  }

  &::after {
    --x: -50%;
    --y: -50%;
    --initialY: -50%;
    transform: translate(-50%, -50%) rotate(170deg) scale(1.15) rotateY(-30deg);
  }

  ${({ colors }) => generateColorVars(colors)}
`;

const Fireworks = () => {
  return (
    <Wrapper>
      <Firework
        colors={COLOR_SET_1}
        x="30vmin"
        y="60vmin"
        top="60%"
        left="30%"
        animationDelay="0"
      />
      <Firework
        colors={COLOR_SET_2}
        x="10vmin"
        y="50vmin"
        top="60%"
        left="30%"
        animationDelay="0.2s"
      />
      <Firework
        colors={COLOR_SET_3}
        x="-30vmin"
        y="-50vmin"
        top="60%"
        left="70%"
        animationDelay="0.4s"
      />
      <Firework colors={COLOR_SET_1} x="0" y="-50vmin" top="70%" left="50%" animationDelay="1s" />
      <Firework
        colors={COLOR_SET_2}
        x="0"
        y="-50vmin"
        top="90%"
        left="60%"
        animationDelay="1.2s"
      />
      <Firework
        colors={COLOR_SET_3}
        x="0"
        y="-50vmin"
        top="70%"
        left="30%"
        animationDelay="1.2s"
      />
    </Wrapper>
  );
};

export default Fireworks;
